import { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import Datetime from 'react-datetime';
import moment from 'moment';
import 'react-datetime/css/react-datetime.css';

// Add inline style to ensure datepicker is positioned correctly with Portal
const datepickerStyle = document.createElement('style');
datepickerStyle.innerHTML = `
  .datepicker-portal-wrapper {
    position: fixed !important;
    z-index: 999999 !important;
  }
  .datepicker-portal-wrapper .rdt {
    position: static !important;
  }
  .datepicker-portal-wrapper .rdtPicker {
    position: static !important;
    display: block !important;
    z-index: 999999 !important;
    box-shadow: 0 2px 10px rgba(0,0,0,0.2);
    background: white;
  }
`;
if (!document.getElementById('datepicker-zindex-fix')) {
    datepickerStyle.id = 'datepicker-zindex-fix';
    document.head.appendChild(datepickerStyle);
}

// Custom Datetime Wrapper Component with Portal
const DatetimeWithPortal = ({ inputProps, onChange, value, ...rest }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [inputPosition, setInputPosition] = useState({ top: 0, left: 0, width: 0 });
    const inputRef = useRef(null);
    const wrapperRef = useRef(null);

    useEffect(() => {
        if (isOpen && inputRef.current) {
            const rect = inputRef.current.getBoundingClientRect();
            setInputPosition({
                top: rect.bottom + 2, // Fixed position, no need for scrollY
                left: rect.left,
                width: rect.width
            });
        }
    }, [isOpen]);

    // Close calendar when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target) &&
                inputRef.current && !inputRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [isOpen]);

    const handleInputClick = () => {
        if (!inputProps?.disabled) {
            setIsOpen(true);
        }
    };

    const handleDateChange = (date) => {
        onChange(date);
        setIsOpen(false);
    };

    return (
        <>
            <input
                {...inputProps}
                ref={inputRef}
                onClick={handleInputClick}
                onFocus={handleInputClick}
                readOnly
            />
            {isOpen && createPortal(
                <div
                    ref={wrapperRef}
                    className="datepicker-portal-wrapper"
                    style={{
                        position: 'fixed',
                        top: inputPosition.top + 'px',
                        left: inputPosition.left + 'px',
                        zIndex: 999999,
                        minWidth: inputPosition.width + 'px'
                    }}
                >
                    <Datetime
                        {...rest}
                        value={value}
                        onChange={handleDateChange}
                        input={false}
                        open={true}
                    />
                </div>,
                document.body
            )}
        </>
    );
}

const DateFilterTable = ({ startDate, endDate, setStartDate, setEndDate, selectedDateType, setSelectedDateType, handleSearch, allDates, setAllDates }) => {

    // Convert YYYY-MM-DD (BE expected) to DD/MM/YYYY for display
    const formatDisplayDate = (dateStr) => {
        if (!dateStr) return '';
        const m = moment(dateStr, 'YYYY-MM-DD');
        return m.isValid() ? m.format('DD/MM/YYYY') : dateStr;
    };

    // Convert DD/MM/YYYY back to YYYY-MM-DD (returns as-is; caller adjusts era)
    const parseInputDate = (dateStr) => {
        if (!dateStr) return '';
        // Try DD/MM/YYYY format first
        let m = moment(dateStr, 'DD/MM/YYYY', true);
        if (!m.isValid()) {
            // Try YYYY-MM-DD format
            m = moment(dateStr, 'YYYY-MM-DD', true);
        }
        return m.isValid() ? m.format('YYYY-MM-DD') : dateStr;
    };

    // Ensure stored date stays in BE (Buddhist Era) so parent can convert to AD reliably
    const toBEString = (adLikeStr) => {
        if (!adLikeStr) return '';
        const m = moment(adLikeStr, 'YYYY-MM-DD', true);
        if (!m.isValid()) return adLikeStr;
        const year = m.year();
        // Heuristic: if year looks AD (< 2400), convert to BE; otherwise assume already BE
        return year < 2400 ? m.add(543, 'year').format('YYYY-MM-DD') : m.format('YYYY-MM-DD');
    };

    // Hide Due Date option on CR/DR pages
    const isCRorDRPage = ["/credit-note", "/debit-note"].includes(window.location.pathname);

    // If currently selected is Due Date on CR/DR, fallback to Document Date
    useEffect(() => {
        if (isCRorDRPage && selectedDateType === "2") {
            setSelectedDateType && setSelectedDateType("1");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isCRorDRPage]);

    return (
        <div
            className="d-flex align-items-center"
            style={{ gap: '6px', whiteSpace: 'nowrap', overflowX: 'visible', width: '100%', position: 'relative' }}
        >
            <div className="form-check d-flex align-items-center mb-0" style={{ height: '32px' }}>
                <input
                    className="form-check-input"
                    type="checkbox"
                    id="chkAllDates"
                    checked={!!allDates}
                    onChange={(e) => setAllDates && setAllDates(e.target.checked)}
                    style={{ marginRight: 4, marginBottom: 0 }}
                />
                <label className="form-check-label mb-0" htmlFor="chkAllDates" style={{ marginRight: 0 }}>
                    ค้นหาวันที่ทั้งหมด
                </label>
            </div>
            <select
                className="form-select form-select-sm"
                aria-label="Select date type"
                value={selectedDateType}
                onChange={(e) => setSelectedDateType(e.target.value)}
                style={{ width: '130px', height: '36px' }}
                disabled={!!allDates}
            >
                <option value="1">วันที่เอกสาร</option>
                {!isCRorDRPage && (<option value="2">วันที่ Due Date</option>)}
            </select>
            <span className='text-black mb-0' style={{ fontSize: '0.875rem' }}>วันที่ :</span>
            <div style={{ position: 'relative' }}>
                <DatetimeWithPortal
                    inputProps={{
                        className: 'form-control form-control-sm',
                        style: { width: '130px', height: '36px', paddingLeft: '8px', paddingRight: '8px' },
                        placeholder: 'DD/MM/YYYY',
                        disabled: !!allDates,
                        value: formatDisplayDate(startDate)
                    }}
                    value={startDate ? moment(startDate, 'YYYY-MM-DD') : ''}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    closeOnSelect={true}
                    onChange={(date) => {
                        if (moment.isMoment(date)) {
                            const beStr = toBEString(date.format('YYYY-MM-DD'));
                            setStartDate && setStartDate(beStr);
                        } else if (typeof date === 'string') {
                            const parsed = parseInputDate(date);
                            const beStr = toBEString(parsed);
                            setStartDate && setStartDate(beStr);
                        }
                    }}
                />
            </div>
            <span className='text-black mb-0' style={{ fontSize: '0.875rem' }}>ถึง :</span>
            <div style={{ position: 'relative' }}>
                <DatetimeWithPortal
                    inputProps={{
                        className: 'form-control form-control-sm',
                        style: { width: '130px', height: '36px', paddingLeft: '8px', paddingRight: '8px' },
                        placeholder: 'DD/MM/YYYY',
                        disabled: !!allDates,
                        value: formatDisplayDate(endDate)
                    }}
                    value={endDate ? moment(endDate, 'YYYY-MM-DD') : ''}
                    dateFormat="DD/MM/YYYY"
                    timeFormat={false}
                    closeOnSelect={true}
                    onChange={(date) => {
                        if (moment.isMoment(date)) {
                            const beStr = toBEString(date.format('YYYY-MM-DD'));
                            setEndDate && setEndDate(beStr);
                        } else if (typeof date === 'string') {
                            const parsed = parseInputDate(date);
                            const beStr = toBEString(parsed);
                            setEndDate && setEndDate(beStr);
                        }
                    }}
                />
            </div>

            <button
                className="btn btn-outline-secondary btn-sm"
                type="button"
                aria-label="Search"
                onClick={handleSearch}
                style={{ height: '36px', width: '36px', padding: '0', lineHeight: 1 }}
            >
                <i className="fas fa-search" />
            </button>
        </div>
    );
};

export default DateFilterTable;